import * as React from 'react';
import * as moment from 'moment';
import {ErrorAlert} from '../common/ErrorAlert';
import {Attachment, ErrorResponse, Item, Rental, SaveInMaintenanceRequest} from '../api/dtos/dtos';
import {RouteComponentProps, withRouter} from 'react-router';
import * as paths from '../paths';
import furoApi from '../api/FuroApi';
import ItemForm from './ItemForm';
import {CURRENCY_EUR} from '../utils/currency-utils';
import {Button, Switch, Tabs} from 'antd';
import locales from '../locales';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import ButtonBar from '../common/ButtonBar';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import {showDeleteSuccessNotification, showSaveConfirm, showSaveSuccessNotification} from 'src/utils/dialogs';
import {RentalState} from "../common/state";
import MaintenanceSheetEditor from './MaintenanceSheetEditor';
import MaintenanceHistoryEditor from "./MaintenanceHistoryEditor";
import ItemStatisticsPage from "../statistics/ItemStatisticsPage";
import ItemInvoicesPage from '../incoming-invoices/ItemInvoicesPage';
import MaintenanceTodoEditor from "./MaintenanceTodoEditor";
import InternallyUsedInventoryItemsTable from './InternallyUsedInventoryItemsTable';
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import accountProvider from "../account/AccountProvider";
import ItemDeleteModal from "./ItemDeleteModal";
import {now, toIsoDateString} from "../utils/date-utils";
import FileUploadEditor from "../common/input/FileUploadEditor";

const {TabPane} = Tabs;
const TAB_SHEET = "sheet";
const TAB_MAINTENANCE = "maintenance";
const TAB_RENTALS = "rentals";
const TAB_STATISTICS = "statistics";
const TAB_INVOICES = "invoices";

interface ItemDetailsPageState {
  editable: boolean;
  showDeleteModal: boolean;
  categories: string[];
  activeTabKey: string;
  item?: Item;
  error?: ErrorResponse;
}

class ItemDetailsPage extends React.Component<RouteComponentProps<paths.IdPageUrlParams>, ItemDetailsPageState> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      editable: false,
      showDeleteModal: false,
      categories: [],
      activeTabKey: TAB_SHEET
    };
  }

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {editable, showDeleteModal, item, activeTabKey, categories, error} = this.state;
    const itemDeleted = (item && item.deleted) === true;
    const admin = accountProvider.isAdmin();
    const sales = accountProvider.isSales();

    const action = item?.id ? furoApi.items.uploadAttachmentsUrl(item.id) : undefined;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Tabs animated={false} activeKey={activeTabKey} onChange={this.onTabChange}>
          <TabPane tab={locales.sheet} key={TAB_SHEET}>
            <ButtonBar style={{marginBottom: 16}}>
              {this.isDeleteButtonVisible() && (admin || sales) && (
                <Button
                  type="danger"
                  icon="delete"
                  onClick={this.onShowDeleteModal}
                  disabled={editable}
                >{locales.delete}</Button>
              )}
              {!itemDeleted && (admin || sales) && (
                <Button
                  type="primary"
                  icon="edit"
                  onClick={this.onEditClick}
                  disabled={editable}
                >{locales.edit}</Button>
              )}
            </ButtonBar>
            {showDeleteModal &&
                <ItemDeleteModal
                    onCancel={this.onHideDeleteModal}
                    onSubmit={this.deleteItem}
                />
            }
            <ItemForm
              item={item}
              categories={categories}
              editable={editable}
              onRefreshExchangeRate={this.refreshExchangeRate}
              onFormSubmit={this.onFormSubmit}
              onFormCancel={this.onFormCancel}
            />
            {item && item.id &&
                <>
                    <SectionHeaderFormItem title={locales.attachments} style={{marginTop: 48}}/>
                    <FileUploadEditor
                        disabled={editable || !(admin || sales)}
                        itemId={item.id}
                        action={action}
                        loadAttachment={this.loadAttachment}
                        deleteAttachment={this.deleteAttachment}
                    />
                </>
            }
          </TabPane>
          {item && (
            <TabPane tab={locales.maintenance} key={TAB_MAINTENANCE}>
              {this.renderMaintenanceToggle(item)}
              <MaintenanceSheetEditor itemId={item.id!}/>
              <MaintenanceHistoryEditor
                item={item}
                onViewHistoryItem={this.onViewMaintenanceHistoryItem}
                onCreateHistoryItem={this.onCreateMaintenanceHistoryItem}
              />
              <InternallyUsedInventoryItemsTable item={item}/>
              <MaintenanceTodoEditor itemId={item.id!}/>
            </TabPane>
          )}
          {item && (admin || sales) && (
            <TabPane tab={locales.statistics} key={TAB_STATISTICS}>
              <ItemStatisticsPage item={item} reload={activeTabKey === TAB_STATISTICS}/>
            </TabPane>
          )}
          {item && admin && (
            <TabPane tab={locales.incoming_invoices} key={TAB_INVOICES}>
              <ItemInvoicesPage item={item} reload={activeTabKey === TAB_INVOICES}/>
            </TabPane>
          )}
          {item && (
            <TabPane tab={locales.rentals} key={TAB_RENTALS}/>
          )}
        </Tabs>
        <ErrorAlert style={{marginTop: 16}} error={error}/>
      </>
    );
  }

  private onTabChange = (activeTabKey: string) => {
    if (activeTabKey === TAB_RENTALS) {
      this.onRentalHistoryClick();
    } else {
      this.setState({activeTabKey});
    }
  };

  private onRentalHistoryClick = () => {
    const {item} = this.state;
    if (item) {
      this.props.history.push(paths.viewItemRentalHistoryPath(item.id!), {appSubtitle: this.getAppSubtitle(item)});
    }
  };

  private loadAttachment = (id: number): Promise<Attachment[]> => {
    return furoApi.items.getAttachments(id);
  }

  private deleteAttachment = (id: number, attachmentId: number): Promise<void> => {
    return furoApi.items.deleteAttachment(id, attachmentId);
  }

  private isDeleteButtonVisible = (): boolean => {
    const {item} = this.state;
    if (!item) {
      return false;
    }
    if (item.deleted) {
      // If item is deleted we don't wanna show the delete button
      return false;
    }
    // If item is rented delete is not possible
    return item.state !== RentalState.RENTED;
  };

  private reload = () => {
    this.loadItem();
    this.loadCategories();
  };

  private getIdParam = (): number => {
    const idParam = this.props.match.params.id;
    return Number(idParam);
  };

  private loadItem = () => {
    const promise = furoApi.items.get(this.getIdParam())
      .then(this.onLoadItemSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadItemSuccess = (response: Item) => {
    this.setState({
      item: response,
      error: undefined,
    });
    setAppHeaderSubtitle(this.props, this.getAppSubtitle(response));
  };

  private getAppSubtitle = (item: Item): string | undefined => {
    return item.inventoryNumber ? `${item.name} - ${item.inventoryNumber}` : item.name;
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});

  private refreshExchangeRate = (): Promise<number | void> => {
    const promise = furoApi.currency.getRate(CURRENCY_EUR, toIsoDateString(now()))
      .then(response => response.rate)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
    return promise;
  };

  private loadCategories = () => {
    const promise = furoApi.items.getCategories()
      .then(response => {
        this.setState({
          categories: response.categories,
          error: undefined,
        });
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onFormSubmit = (item: Item) => {
    showSaveConfirm(() => this.saveFormValues(item));
  };

  private onFormCancel = () => {
    this.setState({editable: false,});
  };

  private onEditClick = () => {
    this.setState({editable: true,});
  };

  private renderMaintenanceToggle = (item: Item): React.ReactNode => {
    const isChecked = item.inMaintenance ? item.inMaintenance : false;
    const onChange = (checked: boolean) => {
      // Note: api call + loading causes a lag in the switch's state change
      setTimeout(() => this.setItemInMaintenance(checked), 300);
    };
    return (
      <ButtonBar>
        <p>{locales.item_in_maintenance}</p>
        <Switch
          size="small"
          defaultChecked={isChecked}
          onChange={onChange}
        />
      </ButtonBar>
    );
  };

  private setItemInMaintenance = (inMaintenance: boolean) => {
    const request: SaveInMaintenanceRequest = {inMaintenance};
    const promise = furoApi.items.setInMaintenance(this.getIdParam(), request)
      .then(() => {
        showSaveSuccessNotification();
        this.loadItem();
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private saveFormValues = (item: Item) => {
    const promise = furoApi.items.save(item, this.getIdParam())
      .then(() => {
        this.setState({
          error: undefined,
          editable: false,
        });
        showSaveSuccessNotification();
        this.reload();
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onShowDeleteModal = () => {
    this.setState({showDeleteModal: true,});
  };

  private onHideDeleteModal = () => {
    this.setState({showDeleteModal: false,});
  };

  private deleteItem = (deleteDate: moment.Moment) => {
    this.onHideDeleteModal();
    const promise = furoApi.items.delete(this.getIdParam(), toIsoDateString(deleteDate))
      .then(() => {
        showDeleteSuccessNotification();
        this.reload();
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onViewMaintenanceHistoryItem = (id: number) => {
    this.props.history.push(paths.viewMaintenanceHistoryItemPath(id));
  }

  private onCreateMaintenanceHistoryItem = (item: Item, rental?: Rental) => {
    this.props.history.push(paths.createMaintenanceHistoryItemPathBase, {rental, item});
  }
}

export default withRouter(ItemDetailsPage);


// WEBPACK FOOTER //
// ./src/item-details/ItemDetailsPage.tsx